function resolveApiBase(): string {
  const raw = ((import.meta.env.VITE_API_BASE as string | undefined) ?? '').trim()
  // Dashboard placeholders like https://<GATEWAY> must not reach fetch().
  if (!raw || raw.includes('<') || raw.includes('>')) return ''
  return raw.replace(/\/$/, '')
}

const API_BASE = resolveApiBase()
const TOKEN_KEY = 'tg_game_blast_jwt'
let memoryToken: string | null | undefined

export function takeTokenFromUrl(): string | null {
  try {
    const u = new URL(window.location.href)
    const fromQuery = u.searchParams.get('token')
    if (fromQuery) {
      memoryToken = fromQuery
      try {
        sessionStorage.setItem(TOKEN_KEY, fromQuery)
      } catch {
        /* ignore */
      }
      u.searchParams.delete('token')
      const qs = u.searchParams.toString()
      window.history.replaceState(null, '', u.pathname + (qs ? `?${qs}` : '') + u.hash)
      return fromQuery
    }
  } catch {
    /* ignore */
  }
  if (memoryToken) return memoryToken
  try {
    const s = sessionStorage.getItem(TOKEN_KEY)
    if (s) {
      memoryToken = s
      return s
    }
  } catch {
    /* ignore */
  }
  return null
}

export async function getInitData(): Promise<string> {
  try {
    const legacy = (
      window as unknown as {
        Telegram?: { WebApp?: { initData?: string; ready?: () => void } }
      }
    ).Telegram?.WebApp
    legacy?.ready?.()
    if (legacy?.initData) return legacy.initData
  } catch {
    /* ignore */
  }
  return ''
}

async function request<T>(
  path: string,
  options: RequestInit & { token?: string } = {},
): Promise<T> {
  if (API_BASE === '' && path.startsWith('/api') && typeof window !== 'undefined') {
    // Production build with placeholder VITE_API_BASE — fail clearly instead of black screen.
    const baked = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''
    if (baked.includes('<') || baked.includes('>')) {
      throw new Error(
        'API base is a placeholder (redeploy with pages-deploy-wrangler.ps1)',
      )
    }
  }
  const headers = new Headers(options.headers)
  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (options.token) headers.set('Authorization', `Bearer ${options.token}`)
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers })
  if (!res.ok) {
    let msg = res.statusText
    try {
      const b = (await res.json()) as { error?: string }
      if (b.error) msg = b.error
    } catch {
      /* ignore */
    }
    throw new Error(msg)
  }
  return (await res.json()) as T
}

export async function loginWithInitData(initData: string) {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { loginDemo } = await import('../../shared/demo/adapters/auth.ts')
    return loginDemo()
  }
  return request<{ token: string; user: { id: number; balance: number } }>(
    '/api/auth/telegram',
    {
      method: 'POST',
      body: JSON.stringify({ init_data: initData }),
    },
  )
}

export async function loginDev() {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { loginDemo } = await import('../../shared/demo/adapters/auth.ts')
    return loginDemo()
  }
  return request<{ token: string; user: { id: number; balance: number } }>(
    '/api/auth/dev',
    {
      method: 'POST',
      body: JSON.stringify({ username: 'local_dev', first_name: 'Local' }),
    },
  )
}

export async function fetchBalance(token: string) {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { fetchBalance: demoBalance } = await import('../../shared/demo/adapters/auth.ts')
    return demoBalance()
  }
  return request<{ balance: number }>('/api/wallet/balance', { token })
}

export type BlastTable = {
  id: string
  room_count: number
  explode_count: number
  min_bet: number
  max_bet: number
}

export async function fetchBlastTables(token: string) {
  if (import.meta.env.VITE_DEMO_MODE === 'true') {
    const { fetchBlastTables: tables } = await import('../../shared/demo/adapters/blast.ts')
    return tables()
  }
  return request<{ tables: BlastTable[]; default?: string }>(
    '/api/games/blast/tables',
    { token },
  )
}

export function readTierFromUrl(): string {
  try {
    const t = new URL(window.location.href).searchParams.get('tier')
    return t && t.trim() ? t.trim() : 'classic_9'
  } catch {
    return 'classic_9'
  }
}

export function writeTierToUrl(tier: string) {
  try {
    const u = new URL(window.location.href)
    if (!tier || tier === 'classic_9') u.searchParams.delete('tier')
    else u.searchParams.set('tier', tier)
    window.history.replaceState(null, '', u.pathname + u.search + u.hash)
  } catch {
    /* ignore */
  }
}

export function userIdFromToken(token: string): number {
  try {
    const part = token.split('.')[1]
    if (!part) return 0
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      user_id?: number
    }
    return Number(json.user_id) || 0
  } catch {
    return 0
  }
}
